import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import GetSaleService from '../../services/getSaleService';
import AuthService from '../../services/authService';
import Sidebar from '../Sidebar/Sidebar';
import ToastService from '../../services/toastService';
import EscPosEncoder from 'esc-pos-encoder';
import { usePrinter } from '../../context/PrinterContext'; // adjust path if needed

const Sales = () => {
  const navigate = useNavigate();

  // ── Printer context ──────────────────────────────────────────
  const { characteristic, isMobile } = usePrinter();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [limit] = useState(10);

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [reprintingId, setReprintingId] = useState(null);

  const [refreshing, setRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const touchStartY = useRef(0);
  const containerRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (!AuthService.isAuthenticated()) navigate('/login');
  }, [navigate]);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchInput]);

  const handleTouchStart = (e) => {
    if (containerRef.current?.scrollTop === 0) {
      touchStartY.current = e.touches[0].clientY;
    }
  };

  const handleTouchMove = (e) => {
    if (refreshing) return;
    const pull = e.touches[0].clientY - touchStartY.current;
    if (pull > 0 && containerRef.current?.scrollTop === 0) {
      e.preventDefault();
      setPullDistance(Math.min(pull, 100));
    }
  };

  const handleTouchEnd = () => {
    if (pullDistance > 60) handleRefresh();
    setPullDistance(0);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchSales(currentPage);
      ToastService.success('Sales refreshed', 1500);
    } catch {
      ToastService.error('Refresh failed');
    } finally {
      setTimeout(() => setRefreshing(false), 500);
    }
  };

  const formatCurrency = (amount) => {
    const num = Number(amount);
    if (isNaN(num)) return 'Rs. 0.00';
    return `Rs. ${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return dateString; }
  };

  const fetchSales = useCallback(async (page = 1) => {
    if (!AuthService.isAuthenticated()) { navigate('/login'); return; }
    setLoading(true);
    setError('');
    try {
      const response = await GetSaleService.getSales(page, limit, searchTerm, startDate, endDate);
      if (response.success) {
        setSales(response.data || []);
        setCurrentPage(response.page || page);
        setTotalPages(response.totalPages || 1);
        setTotalItems(response.totalItems || 0);
      } else {
        throw new Error(response.message || 'Failed to fetch sales');
      }
    } catch (err) {
      setError(err.message);
      setSales([]);
      ToastService.error('Failed to load sales data');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, startDate, endDate, limit, navigate]);

  useEffect(() => {
    setEndDate(new Date().toISOString().split('T')[0]);
    fetchSales(1);
  }, []);

  useEffect(() => {
    if (!loading) fetchSales(1);
  }, [searchTerm, startDate, endDate]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      fetchSales(page);
      containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setStartDate('');
    setEndDate(new Date().toISOString().split('T')[0]);
    setCurrentPage(1);
  };

  const hasActiveFilters = searchInput || startDate || (endDate !== new Date().toISOString().split('T')[0]);

  // ── payment helpers ──────────────────────────────────────────
  const getCashAmount = (sale) =>
    sale.cashMovement?.filter((m) => m.type === 'CASH').reduce((s, m) => s + Number(m.amount), 0) ?? 0;

  const getChequeAmount = (sale) =>
    sale.cashMovement?.filter((m) => m.type === 'CHEQUE').reduce((s, m) => s + Number(m.amount), 0) ?? 0;

  const getCreditAmount = (sale) =>
    sale.credit?.reduce((s, c) => s + Number(c.amount), 0) ?? 0;

  const getReturnAmount = (sale) =>
    sale.return?.reduce((s, r) => s + Number(r.amount), 0) ?? 0;

  const getPaymentLabel = (sale) => {
    const types = [
      getCashAmount(sale) > 0 && 'CASH',
      getChequeAmount(sale) > 0 && 'CHEQUE',
      getCreditAmount(sale) > 0 && 'CREDIT',
    ].filter(Boolean);
    if (types.length > 1) return 'MIXED';
    return types[0] || 'CASH';
  };

  // ── REPRINT ──────────────────────────────────────────────────
  const generateReprintReceipt = (sale) => {
    const encoder = new EscPosEncoder();

    // const businessName = "       Theresa Products";
    // const other = "    Distributor: Perera Brothers ";
    // const businessAddress = "NO 51/17, Kalyani Mawatha, Wattala";
    // const businessPhone = "Tel: +94 77 566 0327";

     const businessName = "         Sayul Redistributions";
  const businessAddress =  "NO 244/2/A.Makola, North, Makola";
  const businessPhone = "Tel: +94 77 222 2700";

    const customerName = (sale.customer?.name || 'Cash Customer').substring(0, 20);
    const invoiceNum = sale.invoiceNumber || '-';

    const saleDate = sale.createdAt
      ? new Date(sale.createdAt).toLocaleDateString('en-GB')
      : new Date().toLocaleDateString('en-GB');
    const saleTime = sale.createdAt
      ? new Date(sale.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
      : new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

    const netTotal = Number(sale.totalValue) || 0;
    const cashAmt = getCashAmount(sale);
    const chequeAmt = getChequeAmount(sale);
    const creditAmt = getCreditAmount(sale);
    const returnAmt = getReturnAmount(sale);
    const paidAmt = cashAmt + chequeAmt;

    const receipt = encoder
      .initialize()
      .codepage('cp858')

      // HEADER
      .align('center')
      .size(2, 2)
      .bold(true)
      .line(businessName)
      .size(1, 1)
      .bold(false)
      .line(businessAddress)
      .line(businessPhone)
      .line('** REPRINT **')
      .line('='.repeat(42))

      // INVOICE DETAILS
      .align('left')
      .bold(true)
      .line('INVOICE DETAILS')
      .bold(false)
      .line('='.repeat(42))
      .line(`Invoice # : ${invoiceNum}`)
      .line(`Date/Time : ${saleDate} ${saleTime}`)
      .line(`Customer  : ${customerName}`)
      .line(`Inventory : ${(sale.inventory?.name || '-').substring(0, 15)}`)
      .line('='.repeat(42))

      // PRODUCTS
      .align('center')
      .bold(true)
      .line('PRODUCTS')
      .bold(false)
      .line('='.repeat(42))
      .bold(true)
      .line('Product              Qty   Line Total')
      .bold(false)
      .line('='.repeat(42));

    (sale.sale || []).forEach((item) => {
      const qty = Number(item.qty) || 0;
      const unitCost = Number(item.unitCost) || 0;
      const lineTotal = Number(item.price) || qty * unitCost;
      const product = (item.product?.name || 'Item').substring(0, 20).padEnd(20);
      const qtyStr = qty.toString().padStart(4);
      const total = lineTotal.toFixed(2).padStart(12);
      receipt.line(`${product}${qtyStr}${total}`);
    });

    receipt.line('='.repeat(42));

    if (returnAmt > 0) {
      receipt.align('right').line(`Returns  : -${returnAmt.toFixed(2).padStart(10)}`);
    }

    receipt
      .align('right')
      .bold(true)
      .line('='.repeat(42))
      .line(`TOTAL: Rs.${netTotal.toFixed(2).padStart(10)}`)
      .bold(false)
      .line('='.repeat(42));

    // PAYMENT METHODS
    receipt.align('left').bold(true).line('PAYMENT METHODS').bold(false).line('='.repeat(42));
    if (cashAmt > 0)   receipt.line(`${'CASH'.padEnd(16)}${cashAmt.toFixed(2).padStart(10)}`);
    if (chequeAmt > 0) receipt.line(`${'CHEQUE'.padEnd(16)}${chequeAmt.toFixed(2).padStart(10)}`);
    if (creditAmt > 0) receipt.line(`${'CREDIT'.padEnd(16)}${creditAmt.toFixed(2).padStart(10)}`);

    // Cheque references
    const chequeMovements = sale.cashMovement?.filter(m => m.type === 'CHEQUE') || [];
    if (chequeMovements.length > 0) {
      receipt.line('-'.repeat(42));
      chequeMovements.forEach(m => receipt.line(`  Cheque Ref: ${m.reference || '-'}`));
    }

    // Credit status
    if (creditAmt > 0) {
      (sale.credit || []).forEach(c => {
        const rem = c.remainingAmount > 0 ? `  Rem: ${Number(c.remainingAmount).toFixed(2)}` : '';
        receipt.line(`  Credit Status: ${c.status}${rem}`);
      });
    }

    receipt
      .line('='.repeat(42))
      .align('left')
      .line(`Paid Amount: ${paidAmt.toFixed(2).padStart(10)}`);

    if (creditAmt > 0) {
      receipt.line(`Credit     : ${creditAmt.toFixed(2).padStart(10)}`);
    }

    // FOOTER
    receipt
      .line('='.repeat(42))
      .line(' ')
      .align('center')
      .bold(true)
      .line('THANK YOU!')
      .bold(false)
      .line('We value your business')
      .line('Please keep this receipt for your records')
      .line(' ')
      .size(1, 1)
      .line('software by @fidesm.dev | +94 75 718 0225')
      .newline()
      .cut('partial');

    return receipt.encode();
  };

  const handleReprint = async (sale) => {
    if (!characteristic || !characteristic.writeValue) {
      ToastService.error('No printer connected. Please connect your printer first.');
      return;
    }

    setReprintingId(sale.id);
    try {
      const receiptData = generateReprintReceipt(sale);

      // Same chunk logic as PrinterContext.printReceipt()
      const CHUNK_SIZE = isMobile ? 100 : 512;
      const totalBytes = receiptData.byteLength;
      let offset = 0;

      while (offset < totalBytes) {
        const chunkSize = Math.min(CHUNK_SIZE, totalBytes - offset);
        const chunk = receiptData.slice(offset, offset + chunkSize);
        await characteristic.writeValue(chunk);
        if (isMobile) {
          await new Promise(resolve => setTimeout(resolve, 80));
        }
        offset += chunkSize;
      }

      ToastService.success('Receipt reprinted successfully!');
    } catch (err) {
      console.error('Reprint failed:', err);
      ToastService.error('Reprint failed: ' + (err.message || 'Unknown error'));
    } finally {
      setReprintingId(null);
    }
  };
  // ─────────────────────────────────────────────────────────────

  if (loading && sales.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading sales data...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-gray-50 overflow-y-auto"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Pull to Refresh */}
      {pullDistance > 0 && (
        <div
          className="fixed top-0 left-0 right-0 flex items-center justify-center z-50 transition-all duration-200"
          style={{ transform: `translateY(${pullDistance}px)`, opacity: pullDistance / 100 }}
        >
          <div className={`bg-white rounded-full p-2 shadow-lg ${refreshing ? 'animate-spin' : ''}`}>
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#0613cc] shadow-lg">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <button className="p-2 -ml-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg lg:hidden"
                onClick={() => setSidebarOpen(true)}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="text-lg sm:text-xl font-semibold text-white">Sales History</h1>
            </div>
            <div className="flex items-center gap-2">
              {/* Printer status pill */}
              <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                characteristic ? 'bg-green-400/20 text-green-200' : 'bg-white/10 text-white/50'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${characteristic ? 'bg-green-400 animate-pulse' : 'bg-white/30'}`} />
                {characteristic ? 'Printer Ready' : 'No Printer'}
              </span>
              <span className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium bg-white/20 text-white">
                Total: {totalItems}
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto">

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-4 sm:p-6 mb-6 border border-gray-100">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              Filters
            </h3>
            <button onClick={clearFilters} disabled={!hasActiveFilters}
              className="px-3 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 disabled:opacity-50 transition-all">
              Clear Filters
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search Transactions</label>
              <div className="relative">
                <input type="text"
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border-2 border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Search invoice, customer..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <input type="date" className="flex-1 px-4 py-2.5 rounded-lg border-2 border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                <span className="hidden sm:block text-gray-500 text-sm">to</span>
                <input type="date" className="flex-1 px-4 py-2.5 rounded-lg border-2 border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-600">
            Showing <span className="font-semibold text-gray-900">{sales.length}</span> of{' '}
            <span className="font-semibold text-gray-900">{totalItems}</span> transactions
            {searchInput && (
              <span className="ml-2 px-2 py-1 rounded-md bg-blue-100 text-blue-700 text-xs font-medium">
                Search: "{searchInput}"
              </span>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3">
            <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm text-red-800">{error}</span>
          </div>
        )}

        {/* Sales Cards */}
        {sales.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center border border-gray-100">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
              <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No sales found</h3>
            <p className="text-gray-600 mb-6">No transactions matching your criteria</p>
            {hasActiveFilters && (
              <button onClick={clearFilters}
                className="px-6 py-3 rounded-lg font-semibold text-white bg-[#0613cc] hover:opacity-90 transition-all">
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:gap-6">
            {sales.map((sale) => {
              const paymentLabel = getPaymentLabel(sale);
              const cashAmt = getCashAmount(sale);
              const chequeAmt = getChequeAmount(sale);
              const creditAmt = getCreditAmount(sale);
              const returnAmt = getReturnAmount(sale);
              const isReprinting = reprintingId === sale.id;

              return (
                <div key={sale.id} className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow overflow-hidden border border-gray-100">
                  {/* Card Header */}
                  <div className="bg-[#0613cc] px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-white">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-sm font-semibold">{sale.invoiceNumber}</span>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      paymentLabel === 'CASH' ? 'bg-green-100 text-green-700' :
                      paymentLabel === 'CREDIT' ? 'bg-orange-100 text-orange-700' :
                      paymentLabel === 'CHEQUE' ? 'bg-blue-100 text-blue-700' :
                      'bg-purple-100 text-purple-700'
                    }`}>
                      {paymentLabel}
                    </span>
                  </div>

                  {/* Card Body */}
                  <div className="p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                      <div>
                        <div className="flex items-center gap-1 mb-1">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="text-xs text-gray-500 font-medium">Customer</span>
                        </div>
                        <p className="text-sm font-semibold text-gray-900">{sale.customer?.name || 'N/A'}</p>
                        <p className="text-xs text-gray-500">{sale.customer?.shopName || ''}</p>
                      </div>

                      <div>
                        <div className="flex items-center gap-1 mb-1">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                          </svg>
                          <span className="text-xs text-gray-500 font-medium">Inventory</span>
                        </div>
                        <p className="text-sm font-semibold text-gray-900">{sale.inventory?.name || 'N/A'}</p>
                        <p className="text-xs text-gray-500">{sale.inventory?.code || ''}</p>
                      </div>

                      <div>
                        <div className="flex items-center gap-1 mb-1">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-xs text-gray-500 font-medium">Total Value</span>
                        </div>
                        <p className="text-lg font-bold text-green-600">{formatCurrency(sale.totalValue)}</p>
                        <p className="text-xs text-gray-500">Items: {sale.totalItems}</p>
                      </div>
                    </div>

                    {/* Payment Breakdown */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 py-3 border-t border-b border-gray-100 mb-4">
                      <div className="text-center">
                        <div className="text-xs text-gray-500 mb-0.5">Cash</div>
                        <div className="text-sm font-bold text-green-600">{formatCurrency(cashAmt)}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-500 mb-0.5">Cheque</div>
                        <div className="text-sm font-bold text-blue-600">{formatCurrency(chequeAmt)}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-500 mb-0.5">Credit</div>
                        <div className="text-sm font-bold text-orange-600">{formatCurrency(creditAmt)}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-500 mb-0.5">Returns</div>
                        <div className="text-sm font-bold text-red-500">{formatCurrency(returnAmt)}</div>
                      </div>
                    </div>

                    {/* Date & Products */}
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                      <span>{formatDate(sale.createdAt)}</span>
                      <span className="truncate max-w-[60%] text-right">
                        {sale.sale?.map(s => s.product?.name).join(', ') || 'N/A'}
                      </span>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setSelectedSale(sale); setShowModal(true); }}
                        className="flex-1 px-4 py-2.5 rounded-lg bg-[#0613cc] text-white font-medium hover:opacity-90 transition-all flex items-center justify-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View
                      </button>

                      <button
                        onClick={() => handleReprint(sale)}
                        disabled={isReprinting || !characteristic}
                        title={!characteristic ? 'Connect printer first' : 'Reprint receipt'}
                        className={`px-4 py-2.5 rounded-lg font-medium transition-all flex items-center justify-center gap-2 min-w-[110px] ${
                          characteristic
                            ? 'bg-gray-700 text-white hover:bg-gray-800 disabled:opacity-60'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        {isReprinting ? (
                          <>
                            <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Printing...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                            </svg>
                            Reprint
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white rounded-xl shadow-md p-4">
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || loading}
              className="w-full sm:w-auto px-4 py-2.5 rounded-lg bg-[#0613cc] text-white font-medium hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous
            </button>
            <div className="text-center">
              <p className="text-sm font-semibold text-gray-900">Page {currentPage} of {totalPages}</p>
              <p className="text-xs text-gray-500">{sales.length} of {totalItems} transactions</p>
            </div>
            <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || loading}
              className="w-full sm:w-auto px-4 py-2.5 rounded-lg bg-[#0613cc] text-white font-medium hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2">
              Next
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </main>

      {/* ── Invoice Modal ──────────────────────────────────────────── */}
      {showModal && selectedSale && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
          <div className="bg-white w-full sm:rounded-2xl shadow-2xl sm:max-w-2xl sm:my-8 min-h-screen sm:min-h-0 max-h-screen sm:max-h-[90vh] flex flex-col">

            <div className="bg-[#0613cc] px-4 sm:px-6 py-4 sm:rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
              <div>
                <h3 className="text-lg font-semibold text-white">Invoice Details</h3>
                <p className="text-xs text-white/80 mt-0.5">{selectedSale.invoiceNumber}</p>
              </div>
              <button onClick={() => { setShowModal(false); setSelectedSale(null); }}
                className="w-9 h-9 rounded-lg bg-white/20 text-white hover:bg-white/30 flex items-center justify-center">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-1 font-medium">CUSTOMER</p>
                  <p className="text-base font-bold text-gray-900">{selectedSale.customer?.name || 'N/A'}</p>
                  <p className="text-sm text-gray-600">{selectedSale.customer?.shopName}</p>
                  <p className="text-xs text-gray-500">{selectedSale.customer?.town}, {selectedSale.customer?.province}</p>
                  <p className="text-xs text-gray-500">{selectedSale.customer?.contactNumber}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-1 font-medium">INVOICE INFO</p>
                  <p className="text-base font-bold text-gray-900">{selectedSale.invoiceNumber}</p>
                  <p className="text-sm text-gray-600">{formatDate(selectedSale.createdAt)}</p>
                  <p className="text-xs text-gray-500 mt-1">Inventory: {selectedSale.inventory?.name}</p>
                  <p className="text-xs text-gray-500">Items: {selectedSale.totalItems}</p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                <p className="text-sm font-semibold text-gray-900 mb-3">Payment Summary</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500 mb-1">Cash</p>
                    <p className="text-sm font-bold text-green-600">{formatCurrency(getCashAmount(selectedSale))}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500 mb-1">Cheque</p>
                    <p className="text-sm font-bold text-blue-600">{formatCurrency(getChequeAmount(selectedSale))}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500 mb-1">Credit</p>
                    <p className="text-sm font-bold text-orange-600">{formatCurrency(getCreditAmount(selectedSale))}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500 mb-1">Returns</p>
                    <p className="text-sm font-bold text-red-500">{formatCurrency(getReturnAmount(selectedSale))}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between bg-white rounded-lg px-4 py-2.5">
                  <span className="text-sm font-semibold text-gray-700">Total Value</span>
                  <span className="text-lg font-bold text-gray-900">{formatCurrency(selectedSale.totalValue)}</span>
                </div>
              </div>

              {selectedSale.cashMovement?.some(m => m.type === 'CHEQUE') && (
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <p className="text-sm font-semibold text-gray-900 mb-3">Cheque Details</p>
                  <div className="space-y-2">
                    {selectedSale.cashMovement.filter(m => m.type === 'CHEQUE').map((m) => (
                      <div key={m.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2">
                        <span className="text-xs text-gray-500">Ref: <span className="font-semibold text-gray-700">{m.reference || '-'}</span></span>
                        <span className="text-sm font-bold text-blue-600">{formatCurrency(m.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedSale.credit?.length > 0 && (
                <div className="bg-orange-50 rounded-xl p-4 border border-orange-100">
                  <p className="text-sm font-semibold text-gray-900 mb-3">Credit Details</p>
                  <div className="space-y-2">
                    {selectedSale.credit.map((c) => (
                      <div key={c.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2">
                        <div>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            c.status === 'PAID' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                          }`}>{c.status}</span>
                          {c.remainingAmount > 0 && (
                            <span className="ml-2 text-xs text-gray-500">Remaining: {formatCurrency(c.remainingAmount)}</span>
                          )}
                        </div>
                        <span className="text-sm font-bold text-orange-600">{formatCurrency(c.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedSale.return?.length > 0 && (
                <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                  <p className="text-sm font-semibold text-gray-900 mb-3">Returns</p>
                  <div className="space-y-2">
                    {selectedSale.return.map((r) => (
                      <div key={r.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2">
                        <span className="text-xs text-gray-500">{formatDate(r.createdAt)}</span>
                        <span className="text-sm font-bold text-red-500">-{formatCurrency(r.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm font-semibold text-gray-900 mb-3">
                  Products ({selectedSale.sale?.length || 0})
                </p>
                <div className="space-y-3">
                  {selectedSale.sale?.map((item) => (
                    <div key={item.id} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <p className="font-semibold text-gray-900">{item.product?.name || 'N/A'}</p>
                          <p className="text-xs text-gray-500">SKU: {item.product?.sku || 'N/A'}</p>
                          <p className="text-xs text-gray-500">Invoice: {item.invoiceNumber}</p>
                        </div>
                        <p className="text-lg font-bold text-green-600 whitespace-nowrap">
                          {formatCurrency(item.price)}
                        </p>
                      </div>
                      <div className="flex items-center justify-between text-sm pt-2 border-t border-gray-200">
                        <span className="text-gray-600">Qty: <span className="font-semibold">{item.qty}</span></span>
                        
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          item.paymentMethod === 'CASH' ? 'bg-green-100 text-green-700' :
                          item.paymentMethod === 'CREDIT' ? 'bg-orange-100 text-orange-700' :
                          item.paymentMethod === 'MIXED' ? 'bg-purple-100 text-purple-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>{item.paymentMethod}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 sm:px-6 py-4 sm:rounded-b-2xl flex gap-2">
              <button
                onClick={() => handleReprint(selectedSale)}
                disabled={reprintingId === selectedSale.id || !characteristic}
                title={!characteristic ? 'Connect printer first' : 'Reprint receipt'}
                className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
                  characteristic
                    ? 'bg-gray-700 text-white hover:bg-gray-800 disabled:opacity-60'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                {reprintingId === selectedSale.id ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Printing...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    {characteristic ? 'Reprint Receipt' : 'No Printer'}
                  </>
                )}
              </button>

              <button
                onClick={() => { setShowModal(false); setSelectedSale(null); }}
                className="flex-1 px-6 py-3 rounded-lg bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sales;
